import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel,
  Typography,
  Checkbox,
  FormControlLabel,
  Grid,
  useMediaQuery,
  Paper,
  IconButton,
  ListItemText,
  Chip
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { nl as nlLocale } from 'date-fns/locale'; // For Dutch/Flemish
import { styled } from '@mui/material/styles';
import ModalUnstyled from '@mui/base/ModalUnstyled';
import Colors from '../../library/Colors';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

// Styled components
const StyledModal = styled(ModalUnstyled)`
  position: fixed;
  text-align: center;
  z-index: 1300;
  right: 0;
  bottom: 0;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const Backdrop = styled('div')`
  z-index: -1;
  position: fixed;
  right: 0;
  bottom: 0;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.5);
  -webkit-tap-highlight-color: transparent;
`;

const StyledContainer = styled(Box)(({ theme, screenWidth }) => ({
  backgroundColor: 'background.paper',
  border: '0px solid #000',
  borderRadius: 6,
  padding: '16px 32px 24px 32px',
  overflowY: 'auto',
  maxHeight: '90vh',
  width: screenWidth < 600 ? '95%' : screenWidth < 960 ? '90%' : '85%',
  maxWidth: '1600px'
}));

const ImagePreviewContainer = styled(Box)(({ theme }) => ({
  border: '1px dashed #ccc',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: '16px',
  height: '100%',
  minHeight: '400px',
  maxHeight: '600px',
  overflow: 'hidden'
}));

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const CustomerDisplayModal = ({ 
  open, 
  onClose,
  screenWidth,
  theme, 
  t,
  stores = [],
  posList = [],
  onSave,
  defaultDuration,
  modalContent,
  modalData,
  imageBaseUrl
}) => {
    // State for image preview
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  // Responsive breakpoints
  const isMobile = useMediaQuery('(max-width:600px)');

  // Create default values with specific times
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  const endOfYear = new Date(today.getFullYear(), 11, 31);


  // Set display_from to today at 00:01
  const initialDisplayFrom = new Date(today);
  initialDisplayFrom.setHours(0, 1, 0, 0); // 00:01:00.000

  // Set display_until to tomorrow at 23:59
  const initialDisplayUntil = new Date(endOfYear);
  initialDisplayUntil.setHours(23, 59, 0, 0); // 23:59:00.000

  // State for form data
  const defaultFormData = {
      id: 0,
      title: '',
      store_ids: [],
      pos_ids: [],
      display_from: initialDisplayFrom,
      display_until: initialDisplayUntil,
      display_type: false,
      sort: 50,
      duration: defaultDuration,
      filename: '',
      imageBase64: '', // Field for base64 image data
      oldFileName: '' 
  }
  const [formData, setFormData] = useState(defaultFormData);
   // Store the last valid dates to persist them
  const [lastDisplayFrom, setLastDisplayFrom] = useState(initialDisplayFrom);
  const [lastDisplayUntil, setLastDisplayUntil] = useState(initialDisplayUntil);

  useEffect(() => {
    if (modalContent === 'edit' && modalData) {
      // Set up the form data as before
      const updatedFormData = {
        ...modalData,
        store_ids: modalData.store_ids ? modalData.store_ids.split(',').map(id => id.trim()) : [],
        pos_ids: modalData.pos_ids ? modalData.pos_ids.split(',').map(id => id.trim()) : [],
        display_type: modalData.display_type == "FULL",
        display_from: modalData.display_from ? new Date(modalData.display_from) : initialDisplayFrom,
        display_until: modalData.display_until ? new Date(modalData.display_until) : null
      };

      // Update the last valid dates
      setLastDisplayFrom(updatedFormData.display_from);
      setLastDisplayUntil(updatedFormData.display_until);
      
      // Set up image preview if filename exists
      if (modalData.filename && imageBaseUrl) {
        let baseUrl = imageBaseUrl;
        
        // Ensure the baseUrl has https if not present
        if (!baseUrl.startsWith('http://') && !baseUrl.startsWith('https://')) {
          baseUrl = 'https://' + baseUrl;
        }
        
        // Make sure baseUrl ends with a slash if it doesn't already
        if (!baseUrl.endsWith('/')) {
          baseUrl += '/';
        }
        
        const imageUrl = baseUrl + modalData.filename;
        setPreviewUrl(imageUrl);
        setSelectedImage({ name: modalData.filename });
        
        updatedFormData.imageBase64 = ''; // Clear any existing base64 data
        
        // Set a flag to indicate we're using an existing image
        updatedFormData.useExistingImage = true;
      }
      
      setFormData(updatedFormData);
    }else{
      let resetFormData = {
        ...defaultFormData,
        display_from: lastDisplayFrom ? lastDisplayFrom : initialDisplayFrom,
        display_until: lastDisplayUntil ? lastDisplayUntil : initialDisplayUntil,
      };      
      setFormData(resetFormData);
      setSelectedImage(null);
      setPreviewUrl(null);
    }
  }, [modalContent, modalData, imageBaseUrl, open]);

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Handle date changes
  const handleDateChange = (date, field) => {
    setFormData({
      ...formData,
      [field]: date
    });
    
    if (field === 'display_from') {
      setLastDisplayFrom(date);
    } else if (field === 'display_until') {
      setLastDisplayUntil(date);
    }
  };
  
  // Handle image selection and convert to base64
  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
      
      // Convert image to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          filename: addTimeRandomToFileName(file.name),
          imageBase64: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const addTimeRandomToFileName = (filename) => {
    const now = new Date();
    const timestamp = now.getFullYear().toString() +
                      (now.getMonth() + 1).toString().padStart(2, '0') +
                      now.getDate().toString().padStart(2, '0') +
                      now.getHours().toString().padStart(2, '0') +
                      now.getMinutes().toString().padStart(2, '0') +
                      now.getSeconds().toString().padStart(2, '0');

    const lastDotIndex = filename.lastIndexOf('.');
    const nameWithoutExt = lastDotIndex !== -1 ? filename.substring(0, lastDotIndex) : filename;
    const extension = lastDotIndex !== -1 ? filename.substring(lastDotIndex) : '';

    filename = `${nameWithoutExt}_${timestamp}${extension}`;
    return filename;
  }
  
  // Handle form submission
  const handleSubmit = () => {
    // Call the onSave callback with the form data (now includes imageBase64)
    onSave(formData);
  };
  
  // Clean up preview URL when component unmounts
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  return (
    <StyledModal
      aria-labelledby="customer-display-modal"
      open={open}
      onClose={onClose}
      BackdropComponent={Backdrop}
    >

    <StyledContainer screenWidth={screenWidth} className={`${theme}_forced`}>
      <Box sx={{ position: 'relative', mb: 2 }}>
        <IconButton 
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: -12,
            top: -12,
            color: theme === 'light' ? Colors.light_text : Colors.dark_text,
          }}
        >
          <CloseIcon />
        </IconButton>
        <Typography 
          variant="h6" 
          className="primary_border_bottom text_center margin_top_none margin_bottom_15px bold"
          style={{ fontWeight: 1200, paddingBottom: '10px' }}
        >
          {modalContent === 'edit' ? t("customer_display_modal_edit_title") : t("customer_display_modal_title")}
        </Typography>
      </Box>
        
        <Grid container spacing={3} direction={isMobile ? "column" : "row"}>
          {/* Left side - Image upload */}
          <Grid item xs={12} md={5} style={{ height: isMobile ? 'auto' : '100%' }}>
            <Paper elevation={0} style={{ height: '100%' }}>
              <ImagePreviewContainer>
                {previewUrl ? (
                  <img 
                    src={previewUrl} 
                    alt="Preview" 
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: isMobile ? '350px' : '550px',
                      objectFit: 'contain'
                    }} 
                  />
                ) : (
                  <Typography variant="body1" color="textSecondary">
                    {t("customer_display_no_image") || "No image selected"}
                  </Typography>
                )}
              </ImagePreviewContainer>

              <Button
                component="label"
                variant="contained"
                tabIndex={-1}
                startIcon={<CloudUploadIcon />}
              >
               {t("customer_display_upload")}
                <VisuallyHiddenInput
                  type="file"
                  onChange={handleImageChange}
                  multiple
                />
              </Button>
    
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={formData.display_type}
                    onChange={handleChange}
                    name="display_type"
                  />
                }
                label={t("customer_display_full_screen")}
              />
            </Paper>
          </Grid>
          
          {/* Right side - Form fields */}
          <Grid item xs={12} md={7} >
            {/* Name and Sort Fields */}
            <Grid container spacing={2} style={{ textAlign: 'left'}}>
              <Grid item xs={12} sm={8} >
                <TextField
                  fullWidth
                  margin="normal"
                  label={t("customer_display_name")}
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className={`${theme}_accent ${theme}_text`}
                  sx={(theme === 'light') ? {
                    '& .MuiOutlinedInput-input': {
                      color: Colors.light_text
                    },
                    '& .MuiInputLabel-root': {
                      color: Colors.light_text
                    }
                  } : {
                    '& .MuiOutlinedInput-input': {
                      color: Colors.dark_text
                    },
                    '& .MuiInputLabel-root': {
                      color: Colors.dark_text_accent
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={2} >
                <TextField
                  fullWidth
                  margin="normal"
                  label={t("customer_display_sort")}
                  name="sort"
                  value={formData.sort || ''}
                  onChange={handleChange}
                  type="number"
                  inputProps={{ min: 0 }}
                  className={`${theme}_accent ${theme}_text`}
                  sx={(theme === 'light') ? {
                    '& .MuiOutlinedInput-input': {
                      color: Colors.light_text
                    },
                    '& .MuiInputLabel-root': {
                      color: Colors.light_text
                    }
                  } : {
                    '& .MuiOutlinedInput-input': {
                      color: Colors.dark_text
                    },
                    '& .MuiInputLabel-root': {
                      color: Colors.dark_text_accent
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  margin="normal"
                  label={t("customer_display_duration")}
                  name="duration"
                  value={formData.duration || defaultDuration || ''}
                  onChange={handleChange}
                  type="number"
                  inputProps={{ min: 0 }}
                  className={`${theme}_accent ${theme}_text`}
                  sx={(theme === 'light') ? {
                    '& .MuiOutlinedInput-input': {
                      color: Colors.light_text
                    },
                    '& .MuiInputLabel-root': {
                      color: Colors.light_text
                    }
                  } : {
                    '& .MuiOutlinedInput-input': {
                      color: Colors.dark_text
                    },
                    '& .MuiInputLabel-root': {
                      color: Colors.dark_text_accent
                    }
                  }}
                />
              </Grid>
            </Grid>
            
            <Grid container spacing={2} style={{ textAlign: 'left'}}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal" style={{marginTop: '0px'}}>
                  <InputLabel id="store-select-label">{t("customer_display_store")}</InputLabel>
                  <Select
                    labelId="store-select-label"
                    label={t("customer_display_store")}
                    name="store_ids"
                    value={formData.store_ids || []}
                    onChange={handleChange}
                    className={`${theme}_accent ${theme}_text`}
                    multiple
                    MenuProps={MenuProps}
                    renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                            <Chip key={value} label={value} className={theme+"_accent_darker "+theme+"_text"}/>
                        ))}
                        </Box>
                    )}
                  >
                    {stores.map((store) => (
                      <MenuItem key={store.code} value={store.code}>
                        <Checkbox checked={formData.store_ids.indexOf(store.code) > -1} />
                        <ListItemText primary={store.code+" - "+store.oms}/>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal" style={{marginTop: '0px'}}>
                  <InputLabel id="pos-select-label">{t("customer_display_pos")}</InputLabel>
                  <Select
                    labelId="pos-select-label"
                    label={t("customer_display_pos")}
                    name="pos_ids"
                    value={formData.pos_ids || []}
                    onChange={handleChange}
                    className={`${theme}_accent ${theme}_text`}
                    multiple
                    MenuProps={MenuProps}
                    renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                            <Chip key={value} label={value} className={theme+"_accent_darker "+theme+"_text"}/>
                        ))}
                        </Box>
                    )}
                  >
                    {posList.map((pos) => (
                      <MenuItem key={pos.code} value={pos.code}>
                        <Checkbox checked={formData.pos_ids.indexOf(pos.code) > -1} />
                        <ListItemText primary={pos.code+" - "+pos.oms}/>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <Grid container spacing={2} style={{ textAlign: 'left'}}>
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={nlLocale}>
                  <DateTimePicker
                    label={t("customer_display_from")}
                    value={formData.display_from}
                    onChange={(date) => handleDateChange(date, 'display_from')}
                    className={`${theme}_accent ${theme}_text` + ' date_picker_form'}
                    format="dd/MM/yyyy HH:mm" 
                    ampm={false}
                    renderInput={(params) => 
                      <TextField 
                        {...params} 
                        fullWidth 
                        margin="normal"
                        sx={(theme === 'light') ? {
                          '& .MuiOutlinedInput-input': {
                            color: Colors.light_text
                          },
                          '& .MuiInputLabel-root': {
                            color: Colors.light_text
                          }
                        } : {
                          '& .MuiOutlinedInput-input': {
                            color: Colors.dark_text
                          },
                          '& .MuiInputLabel-root': {
                            color: Colors.dark_text_accent
                          }
                        }}
                      />
                    }
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={nlLocale}>
                  <DateTimePicker
                    label={t("customer_display_until")}
                    value={formData.display_until}
                    onChange={(date) => handleDateChange(date, 'display_until')}
                    className={`${theme}_accent ${theme}_text`  + ' date_picker_form'}
                    format="dd/MM/yyyy HH:mm"
                    ampm={false}
                    renderInput={(params) => 
                      <TextField 
                        {...params} 
                        fullWidth 
                        margin="normal"
                        sx={(theme === 'light') ? {
                          '& .MuiOutlinedInput-input': {
                            color: Colors.light_text
                          },
                          '& .MuiInputLabel-root': {
                            color: Colors.light_text
                          }
                        } : {
                          '& .MuiOutlinedInput-input': {
                            color: Colors.dark_text
                          },
                          '& .MuiInputLabel-root': {
                            color: Colors.dark_text_accent
                          }
                        }}
                      />
                    }
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>

            <Button
              variant="contained"
              fullWidth
              sx={{ mt: 3 }}
              onClick={handleSubmit}
            >
              {t("customer_display_btn_save")}
            </Button>
          </Grid>
        </Grid>
      </StyledContainer>
    </StyledModal>
  );
};

export default CustomerDisplayModal;